import React, { type FC, useState } from 'react'
import { Box, Typography } from '@mui/material'

import { type ColumnView, ConnectwareError, ConnectwareErrorType, StatusType } from '../../../domain'

import { Button, RecordPanel, RecordPanelOutletProvider, Status, Table, type TableActions, type TableColumns } from '../common'

type Line = Readonly<{ id: string, name: string, status: StatusType, endpoints: number, updated: string | null }>
type KeyValueLine = Readonly<{ key: string, value: string }>

const lines: readonly Line[] = [
    { id: 'opcua', name: 'opcua-plant-floor', status: StatusType.ENABLED, endpoints: 12, updated: '2026-09-24 14:02' },
    { id: 'modbus', name: 'modbus-line-3', status: StatusType.DEVIATED, endpoints: 4, updated: '2026-09-23 09:41' },
    {
        id: 'mqtt',
        name: 'mqtt-bridge-with-a-rather-long-name-that-has-to-wrap-inside-its-cell/and/a/topic/like/path',
        status: StatusType.DISABLED,
        endpoints: 0,
        updated: '2026-09-20 17:15',
    },
    { id: 's7', name: 's7-press-hall', status: StatusType.ENABLED, endpoints: 31, updated: '2026-09-19 08:00' },
    { id: 'influx', name: 'influxdb-writer', status: StatusType.UNREACHABLE, endpoints: 2, updated: null },
]

const PAGED_LINE_COUNT = 120

const pagedLines: readonly Line[] = Array.from({ length: PAGED_LINE_COUNT }, (_, index) => ({
    id: `service-${index}`,
    name: `service-${String(index + 1).padStart(String(PAGED_LINE_COUNT).length, '0')}`,
    status: index % 3 === 0 ? StatusType.DISABLED : StatusType.ENABLED,
    endpoints: index,
    updated: '2026-09-24 14:02',
}))

const keyValueLines: readonly KeyValueLine[] = [
    { key: 'Version', value: '2.0.0' },
    { key: 'Uptime', value: '12 days' },
    { key: 'Host', value: 'connectware.local' },
]

const columns: TableColumns<Line> = {
    name: { label: 'Name', sort: true },
    status: { label: 'Status', sort: true, customCellRender: (status) => <Status status={status} /> },
    endpoints: { label: 'Endpoints', sort: true, type: 'number' },
    updated: { label: 'Last updated', type: 'datetime' },
}

const keyValueColumns: TableColumns<KeyValueLine> = { key: {}, value: {} }

const noop = (): void => {}

/** As a resource list would: each action only for the rows it applies to, and none at all for a deviated row */
const selectActions = (selected: readonly Line[]): TableActions<Line> => ({
    ...(selected.every(({ status }) => status === StatusType.DISABLED) ? { ENABLE: noop } : {}),
    ...(selected.every(({ status }) => status === StatusType.ENABLED) ? { DISABLE: noop } : {}),
    ...(selected.some(({ status }) => status === StatusType.DEVIATED) ? {} : { DELETE: noop }),
})

const Sortable: FC = () => <Table columns={columns} data={lines} sortOrder={{ name: 'name', direction: 'asc' }} />

const Clickable: FC = () => <Table columns={columns} data={lines} onRowClick={noop} />

const Selectable: FC = () => (
    <Table
        data-testid="storybook-tables-selectable"
        columns={columns}
        data={lines}
        selection={{ idField: 'id', icons: {}, labels: {}, onSelect: selectActions }}
    />
)

const arrangedColumnView: ColumnView = { order: ['name', 'endpoints', 'status', 'updated'], hidden: ['status'], pinned: ['updated'], widths: { name: 240 } }

const WithColumnView: FC = () => {
    const [columnView, setColumnView] = useState(arrangedColumnView)
    return <Table columns={columns} data={lines} columnView={columnView} onColumnViewChange={setColumnView} />
}

const PaginatedWithTotalCount: FC = () => (
    <Table
        columns={columns}
        data={pagedLines}
        pagination={{ pageSize: 10, pageSizeOptions: [10, 25, 50], showTotalCount: true, totalCount: pagedLines.length }}
        translations={{ paginationDataType: 'SERVICE' }}
    />
)

const PaginatedWithJumpToPage: FC = () => <Table columns={columns} data={pagedLines} pagination={{ pageSize: 10, pageSizeOptions: [10, 25, 50] }} />

const WithToolbar: FC = () => (
    <Table
        columns={columns}
        data={lines}
        filters={
            <Button variant="secondary" size="medium" onClick={noop}>
                Filter
            </Button>
        }
        utilities={
            <Button variant="secondary" size="medium" onClick={noop}>
                Refresh
            </Button>
        }
        onDownloadCSV={noop}
    />
)

const Empty: FC = () => <Table columns={columns} data={[]} translations={{ emptyTable: 'No services installed' }} />

const NoMatch: FC = () => <Table columns={columns} data={lines} search="no-such-service" translations={{ noResults: 'No service matches this search' }} />

const Loading: FC = () => <Table columns={columns} data={[]} loading />

const Reloading: FC = () => <Table columns={columns} data={lines} loading />

const SearchTooShort: FC = () => <Table columns={columns} data={lines} search="ab" searchThreshold={3} />

const loadError = new ConnectwareError(ConnectwareErrorType.SERVER_ERROR, 'The server did not answer', { status: 503 })

const ErrorWithTryAgain: FC = () => <Table columns={columns} data={[]} error={{ error: loadError, onRetry: noop }} />

const KeyValue: FC = () => <Table columns={keyValueColumns} data={keyValueLines} hideHeader />

const ACTIVE_LINE_ID = 'modbus'

const ActiveRow: FC = () => <Table columns={columns} data={lines} onRowClick={noop} isRowActive={({ id }) => id === ACTIVE_LINE_ID} />

const RECORD_PANEL_HEIGHT = 360

const recordPanelAreaStyle = { position: 'relative', height: RECORD_PANEL_HEIGHT, overflow: 'hidden' } as const

/** Its own outlet, so the panel stays over this example rather than the page */
const WithRecordPanel: FC = () => {
    const [outlet, setOutlet] = useState<HTMLElement | null>(null)
    const [open, setOpen] = useState(false)
    const [index, setIndex] = useState(0)
    const [width, setWidth] = useState<number | null>(null)
    const line = lines[index]

    return (
        <Box data-testid="storybook-tables-record-panel" ref={setOutlet} sx={recordPanelAreaStyle}>
            <RecordPanelOutletProvider outlet={outlet}>
                <Table
                    columns={columns}
                    data={lines}
                    onRowClick={(clicked) => {
                        setIndex(lines.indexOf(clicked))
                        setOpen(true)
                    }}
                    isRowActive={(candidate) => open && candidate === line}
                />
                <RecordPanel
                    open={open}
                    title={line?.name}
                    position={{ index, total: lines.length }}
                    onPrevious={index > 0 ? () => setIndex(index - 1) : undefined}
                    onNext={index < lines.length - 1 ? () => setIndex(index + 1) : undefined}
                    onExpand={noop}
                    onClose={() => setOpen(false)}
                    width={width}
                    onWidthChange={setWidth}
                >
                    <Typography variant="body2">Endpoints: {line?.endpoints}</Typography>
                </RecordPanel>
            </RecordPanelOutletProvider>
        </Box>
    )
}

const examples: readonly Readonly<{ label: string, Example: FC }>[] = [
    { label: 'Sortable', Example: Sortable },
    { label: 'Clickable rows', Example: Clickable },
    { label: 'Selectable rows, with row actions', Example: Selectable },
    { label: 'Active row', Example: ActiveRow },
    { label: 'Column view: reordered, pinned, hidden, sized and resizable columns', Example: WithColumnView },
    { label: 'Paginated, with total count', Example: PaginatedWithTotalCount },
    { label: 'Paginated, with jump to page', Example: PaginatedWithJumpToPage },
    { label: 'Toolbar: search, filters and utilities', Example: WithToolbar },
    { label: 'Empty, first run', Example: Empty },
    { label: 'Empty, no match', Example: NoMatch },
    { label: 'Loading', Example: Loading },
    { label: 'Loading, over existing rows', Example: Reloading },
    { label: 'Search too short', Example: SearchTooShort },
    { label: 'Error, with Try again', Example: ErrorWithTryAgain },
    { label: 'Key/value, no header', Example: KeyValue },
    { label: 'Record panel, over its list', Example: WithRecordPanel },
]

const Tables: FC = () => (
    <>
        <Typography variant="subtitle1" sx={{ display: 'block', mb: 4 }}>
            Table
        </Typography>

        {examples.map(({ label, Example }) => (
            <Box key={label} sx={{ mb: 8 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    {label}
                </Typography>
                <Example />
            </Box>
        ))}
    </>
)

export { Tables }
